/**
 * Company Status Chip Component
 * Displays company status with appropriate color coding
 */

import React from 'react';
import { Chip, ChipProps } from '@mui/material';
import {
  CompanyStatus,
  COMPANY_STATUS_LABELS,
  getCompanyStatusColor,
} from '../../../types/company';

interface CompanyStatusChipProps {
  status: CompanyStatus | string;
  size?: ChipProps['size'];
}

/**
 * Maps company status to chip color
 */
const getChipColor = (status: CompanyStatus | string): ChipProps['color'] => {
  // Handle both new enum values and legacy status values
  const normalizedStatus = normalizeStatus(status);
  return getCompanyStatusColor(normalizedStatus);
};

/**
 * Normalizes status to CompanyStatus enum
 * Handles both new English values and legacy Portuguese values
 */
const normalizeStatus = (status: CompanyStatus | string): CompanyStatus => {
  // If already a valid CompanyStatus enum value
  if (Object.values(CompanyStatus).includes(status as CompanyStatus)) {
    return status as CompanyStatus;
  }

  // Map legacy Portuguese values to new English enum
  const legacyMapping: Record<string, CompanyStatus> = {
    'ATIVA': CompanyStatus.ACTIVE,
    'INATIVA': CompanyStatus.INACTIVE,
    'SUSPENSA': CompanyStatus.SUSPENDED,
    'INADIMPLENTE': CompanyStatus.DEFAULTING,
    'BLOQUEADA': CompanyStatus.BLOCKED,
  };

  return legacyMapping[status] || CompanyStatus.ACTIVE;
};

/**
 * Gets the display label for the status
 */
const getStatusLabel = (status: CompanyStatus | string): string => {
  const normalizedStatus = normalizeStatus(status);
  return COMPANY_STATUS_LABELS[normalizedStatus] || status;
};

const CompanyStatusChip: React.FC<CompanyStatusChipProps> = ({
  status,
  size = 'small',
}) => {
  return (
    <Chip
      label={getStatusLabel(status)}
      color={getChipColor(status)}
      size={size}
      sx={{
        fontWeight: 500,
        minWidth: 80,
      }}
    />
  );
};

export default CompanyStatusChip;
